import React, { useState } from 'react';
import { Tag, X, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface AppliedDiscount {
  code: string;
  discountType: 'percentage' | 'fixed_amount';
  discountValue: number;
  description?: string;
  minPurchaseAmount?: number;
}

interface DiscountCodeInputProps {
  cartTotal: number;
  customerEmail?: string;
  onApply: (discount: AppliedDiscount | null) => void;
  appliedDiscount: AppliedDiscount | null;
}

export const DiscountCodeInput: React.FC<DiscountCodeInputProps> = ({
  cartTotal,
  customerEmail,
  onApply,
  appliedDiscount
}) => {
  const [code, setCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  const validateAndApplyCode = async () => {
    if (!code.trim()) {
      toast.error('Please enter a discount code');
      return;
    }

    setIsValidating(true);

    try {
      const { data, error } = await supabase.rpc('validate_discount_code', {
        p_code: code.trim().toUpperCase(),
        p_email: customerEmail || null,
        p_cart_total: cartTotal,
      });

      if (error) {
        console.error('Discount validation error:', error);
        toast.error('Failed to validate discount code');
        return;
      }

      const result = data as {
        valid: boolean;
        error?: string;
        code?: string;
        discount_type?: 'percentage' | 'fixed_amount';
        discount_value?: number;
        description?: string;
        min_purchase_amount?: number;
      };

      if (!result?.valid) {
        toast.error(result?.error || 'Invalid discount code');
        return;
      }

      const applied: AppliedDiscount = {
        code: result.code!,
        discountType: result.discount_type!,
        discountValue: Number(result.discount_value),
        description: result.description || undefined,
        minPurchaseAmount: Number(result.min_purchase_amount || 0),
      };

      onApply(applied);
      toast.success(`Discount applied: ${applied.description || applied.code}`);
      setCode('');
    } catch (err) {
      console.error('Error validating discount code:', err);
      toast.error('Failed to validate discount code');
    } finally {
      setIsValidating(false);
    }
  };

  const removeDiscount = () => {
    onApply(null);
    toast.info('Discount removed');
  };

  const calculateSavings = () => {
    if (!appliedDiscount) return 0;
    
    if (appliedDiscount.discountType === 'percentage') {
      return (cartTotal * appliedDiscount.discountValue) / 100;
    }
    return Math.min(appliedDiscount.discountValue, cartTotal);
  };

  if (appliedDiscount) {
    const savings = calculateSavings();
    return (
      <div className="bg-solr-aurora/10 border border-solr-aurora/30 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-solr-aurora" />
            <div>
              <span className="font-medium text-solr-aurora">{appliedDiscount.code}</span>
              <p className="text-sm text-solr-starfield-silver">
                {appliedDiscount.discountType === 'percentage' 
                  ? `${appliedDiscount.discountValue}% off` 
                  : `£${appliedDiscount.discountValue.toFixed(2)} off`}
                {appliedDiscount.description && ` - ${appliedDiscount.description}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-solr-aurora font-medium">-£{savings.toFixed(2)}</span>
            <button
              onClick={removeDiscount}
              className="text-solr-starfield-silver hover:text-red-400 transition-colors"
              aria-label="Remove discount"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Tag className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-solr-starfield-silver" />
          <Input
            type="text"
            placeholder="Enter discount code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && validateAndApplyCode()}
            className="pl-10 bg-solr-space-dark/50 border-solr-nebula-purple/20 uppercase"
            disabled={isValidating}
          />
        </div>
        <Button
          onClick={validateAndApplyCode}
          disabled={isValidating || !code.trim()}
          variant="outline"
          className="border-solr-nebula-purple/30 hover:bg-solr-nebula-purple/10"
        >
          {isValidating ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            'Apply'
          )}
        </Button>
      </div>
    </div>
  );
};
